// Public surface for `@auctogon/core/realtime`. Production code imports from
// this barrel and `./testing` only — never reach into adapters, types, or
// events directly. Consumers depend on the interfaces, never on a vendor SDK —
// and since BL-1517 there is no vendor SDK left on this path at all: Supabase
// Realtime is retired, and Memorystore + socket.io are ours.

export type { RealtimePublisher } from './types/realtime/publisher';
export type { RealtimeSubscriber } from './types/realtime/subscriber';
export type {
    PublishErrorCode,
    PublishResult,
    Unsubscribe,
} from './types/realtime/types';

export type {
    LedgerAppendedEvent,
    LedgerAppendedHintEvent,
    LotStateDeltaEvent,
    PayloadOf,
    RealtimeEvent,
    RealtimeEventName,
    SnapshotUpdatedEvent,
    SnapshotUpdatedHintEvent,
    TopicOf,
} from './types/realtime/events';

// BUG-1454 — the single source of the "gateway is not configured" verdict and
// its wording, shared by the browser-side inert subscriber and by each app's
// boot check. Import-free by design, so it is safe on this server-reachable
// barrel as well as in the browser.
export {
    REALTIME_GATEWAY_ENV_VAR,
    REALTIME_GATEWAY_MISSING_NOTICE,
    realtimeGatewayMissingNotice,
    realtimeGatewayBootNotice,
} from './isomorphic/realtime/gateway-configuration-notice';

export { redactTopicSecret, topics } from './isomorphic/realtime/topics';
export type { Topic } from './isomorphic/realtime/topics';

// The ONLY publish transport since BL-1517 (ADR 046 step 5). Apps get it
// composed from the env via `@auctogon/core/realtime/create-publisher`, which is
// server-only and therefore not on this barrel; this export is for tests and for
// a caller supplying its own Redis client.
export { createMemorystoreRealtimePublisher } from './transport/realtime/memorystore-publisher';
export type { RedisPublishClient } from './transport/realtime/memorystore-publisher';

// The decorator that emits the `realtime publish outcome` line the
// `realtime_publish_outcome` metric counts. It used to live inside the composite
// publisher, whose fan-out died with the Supabase adapter (BL-1517).
export { withPublishOutcomeLogging } from './transport/realtime/publish-outcome-log';
export type { PublishOutcomeLoggingOptions } from './transport/realtime/publish-outcome-log';

// The gateway's client half. BL-1406 built it as an OBSERVER alongside Supabase;
// BL-1495 made it the delivering transport; BL-1517 removed the transport it was
// observing, so it is now simply THE subscriber. Client-safe — this adapter takes
// a caller-supplied socket and reaches no transport library;
// `createGatewaySubscriber` and `createRealtimeSubscriber` (which do) stay on
// their own subpaths.
export { createSocketIoRealtimeSubscriber } from './transport/realtime/socketio-subscriber';
export type { SocketIoSubscriberOptions, SocketLike } from './transport/realtime/socketio-subscriber';

// Shared by every adapter AND by the gateway's PSUBSCRIBE — one definition, so a
// publisher and a subscriber can never disagree about the prefix.
export { topicToChannel, channelToTopic, CHANNEL_PATTERN } from './isomorphic/realtime/channel-naming';

export {
    RealtimeProvider,
    useRealtimeSubscriber,
} from './components/realtime/RealtimeProvider';

export {
    SnapshotProvider,
    SnapshotStore,
    SnapshotCatchupError,
    defaultSnapshotCatchupFetcher,
    parseSnapshotTopic,
    useSnapshot,
    useSnapshotState,
} from './components/realtime/SnapshotContext';
// BL-2491 — a server-minted private topic, fetched once per key and retried on
// focus / `online`: bidder-web's private topics and the staff Inbox count.
export { createMintedTopicsHook } from './hooks/realtime/minted-topics';
export type { MintedTopicsOptions, MintedTopicsResult } from './hooks/realtime/minted-topics';
// BL-2473 — the one "pop each new toast once" loop both apps' toast listeners run.
export { useToastLedger } from './hooks/realtime/useToastLedger';
export type {
    SnapshotCatchupFetcher,
    SnapshotCatchupResult,
    SnapshotFetchError,
    SnapshotFetcher,
    SnapshotInitialHydration,
    SnapshotProviderProps,
    SnapshotState,
    LegacySnapshotFetcher,
} from './components/realtime/SnapshotContext';

// BL-1465 — the batching decorator over the catch-up fetcher seam. Coalesces
// same-kind calls issued in one task into a single POST to
// `/<kind>/catchup/batch`, collapsing a grid page's per-lot request fan-out.
// Apps normally opt in via `SnapshotProvider`'s / `RealtimeStack`'s
// `batchableKinds` rather than constructing this directly.
export {
    createBatchingSnapshotCatchupFetcher,
    DEFAULT_CLIENT_MAX_BATCH_SIZE,
} from './stores/realtime/batching-catchup-fetcher';
export type { BatchingSnapshotCatchupFetcherOpts } from './stores/realtime/batching-catchup-fetcher';

export {
    LedgerProvider,
    LedgerStore,
    useLedger,
    useLedgerFetchState,
    useLedgerReconcile,
} from './components/realtime/LedgerContext';
export { isSeqCursor } from './stores/realtime/ledger-store';
export type {
    LedgerCursor,
    LedgerFetchState,
    LedgerFetchStatus,
    LedgerInitialFetcher,
    LedgerItem,
    LedgerProviderProps,
    LedgerReconciler,
    UseLedgerOptions,
} from './components/realtime/LedgerContext';

// The one shape a client-side realtime catch-up failure is logged in (BUG-1306).
// Exported because bidder-web's own reconcile catch shares the defect class.
export { formatRealtimeFailure, httpStatusOf } from './isomorphic/realtime/realtime-failure-log';

export { RealtimeStack } from './components/realtime/RealtimeStack';
export type { RealtimeStackProps } from './components/realtime/RealtimeStack';

// ADR 042 §§4–5 (BL-1251) — the admin lot-state consumer contract. One provider
// owns the LWW-per-lot fold + catch-up + transport; `useLots` / `useLot` read it.
export {
    RealtimeLotsProvider,
    useLots,
    useLot,
    // BL-1254 AC 8: connection state for a consumer's health banner. Exposed on
    // the provider because it CANNOT be inferred from `useLots()` — a zero-lot
    // auction is indistinguishable from a dead subscription by lot count.
    useLotsConnection,
    // BL-1542 — the nullable store reader. `useLotStateForRecord` uses it to
    // pick its source; `LotBidRecord` uses it to decide whether to supply its
    // own provider. Exported because that unit lives in another package.
    useOptionalLotStateStore,
} from './components/realtime/RealtimeLotsProvider';
// BL-1542 AC 27–31 / ADR 053 — Console Scale § 4.1 — one lot's state from
// whichever source the surface already owns.
export { useLotStateForRecord } from './hooks/realtime/useLotStateForRecord';
// BL-2253 — the nullable one-lot fold read and connection, for readers that may
// render with no provider above (the lot editor's header on a home lot, the
// opening-bid notice on the create door).
export { useOptionalLot, useOptionalLotsConnection } from './hooks/realtime/useOptionalLot';
export type { LotStateForRecord } from './hooks/realtime/useLotStateForRecord';
export type {
    RealtimeLotsProviderProps,
    UseLotsOptions,
} from './components/realtime/RealtimeLotsProvider';
export {
    LotStateStore,
    applyDeltaToMap,
    foldLotStateDeltas,
    DISCONNECTED_LOT_STATE,
} from './stores/realtime/lot-state-store';
export type {
    LotStateCatchupFetcher,
    LotStateCatchupResult,
    LotStateConnection,
} from './stores/realtime/lot-state-store';

// BL-1262 — the admin auction-registrations consumer contract. Same shape as the
// lot provider above and built on the SAME engine (`react/entity-state-store.ts`):
// one provider owns the LWW-per-registration fold + catch-up + transport, and
// `useRegistrations` reads it. Powers the clerk console's bidder rail, where a
// registration arriving mid-sale must appear without a refresh (AC 2).
export {
    RealtimeRegistrationsProvider,
    useOptionalRegistrationStore,
    useRegistrations,
    useRegistrationsConnection,
} from './components/realtime/RealtimeRegistrationsProvider';
export type {
    RealtimeRegistrationsProviderProps,
    UseRegistrationsOptions,
} from './components/realtime/RealtimeRegistrationsProvider';
export { RegistrationStateStore, DISCONNECTED_REGISTRATION_STATE } from './stores/realtime/registration-state-store';
export type {
    RegistrationStateCatchupFetcher,
    RegistrationStateCatchupResult,
    RegistrationStateConnection,
} from './stores/realtime/registration-state-store';

// BL-1489 — the admin TENANT-scoped conversations trigger. Fourth binding of
// the SAME engine, and the only unsequenced one (wall-clock cursor, no gap
// detection — the channel is a re-read trigger for the clerk's shared inbox and
// renders nothing itself). `useOptionalConversationStore` is the whole consumer
// surface; the inbox folds it to a signature and re-reads REST on change.
export {
    RealtimeConversationsProvider,
    useOptionalConversationStore,
} from './components/realtime/RealtimeConversationsProvider';
export type { RealtimeConversationsProviderProps } from './components/realtime/RealtimeConversationsProvider';
export { ConversationStateStore, DISCONNECTED_CONVERSATION_STATE } from './stores/realtime/conversation-state-store';
export type {
    ConversationStateCatchupFetcher,
    ConversationStateCatchupResult,
    ConversationStateConnection,
} from './stores/realtime/conversation-state-store';

// BL-1325 / BL-1552 — the admin MAX-BID consumer contract (renamed off `PreBids`
// by BUG-1426; scoped from the auction to the LOT by BL-1552). Third binding of
// the SAME engine (`react/entity-state-store.ts`), now ref-counted per lot by
// `react/entity-state-registry.ts`. Powers the clerk console's bid record — both
// the `max $X` chip (`useLotMaxBids` → `selectStandingMaxLadder`) and the pre-bid
// section, where a correction taken in another session must appear without a
// refresh.
//
// The provider is GENERIC — transport only, not auction-keyed — and the hooks
// take the lot id positionally, because the lot IS the subscription key rather
// than a client-side filter. That is what lets a bid-record unit mount outside
// the clerk console (BL-1542's precondition). The auction-wide
// `RealtimeMaxBidsProvider` / `useMaxBids` / `usePreBids` /
// `useMaxBidsConnection` are DELETED, not deprecated (ADR 044 — Must
// Consolidate): keeping `useMaxBids()` over a per-lot subscription would have
// left a caller able to omit the lot id and silently get one lot instead of the
// auction.
export { MaxBidsProvider, type MaxBidsProviderProps } from './components/realtime/MaxBidsProvider';
// BL-1542 — the nullable twin of `useMaxBidRegistry`, so a unit that renders both
// on and off the clerk console can supply its own transport where there is none.
export { useOptionalMaxBidRegistry } from './components/realtime/MaxBidsProvider';
export {
    useLotMaxBids,
    useLotMaxBidsFeedState,
    useLotPreBids,
    type LotMaxBidsFeedState,
    type UseLotPreBidsOptions,
} from './hooks/realtime/useLotMaxBids';
export { EntityStateStoreRegistry } from './stores/realtime/entity-state-registry';
export type { RegistrableEntityStore } from './stores/realtime/entity-state-registry';
export { MaxBidStateStore, DISCONNECTED_MAX_BID_STATE } from './stores/realtime/max-bid-state-store';
export type {
    MaxBidStateCatchupFetcher,
    MaxBidStateCatchupResult,
    MaxBidStateConnection,
} from './stores/realtime/max-bid-state-store';

// BUG-1467 — the PER-LOT de-anon bid ledger (ADR 042 §3, BL-1253). A hook, not
// a provider: it is scoped to the SELECTED lot, so it attaches on select and
// lets go on change. It replaced an auction-wide `RealtimeAdminBidLedgerProvider`
// + `useAdminBidLedger` pair, whose last consumer (the clerk rail's per-bidder
// activity fold) moved to a server-side aggregate in BL-1556 — provider, hook,
// catch-up factory, route and topic all deleted there (ADR 044 — Must
// Consolidate).
export {
    useAdminLotBidLedger,
    BID_RECORD_FEED_ERROR_MESSAGE,
} from './hooks/realtime/useAdminLotBidLedger';
export type {
    AdminLotBidLedgerCatchupFetcher,
    AdminLotBidLedgerValue,
} from './hooks/realtime/useAdminLotBidLedger';

// The shared engine behind both entity-state streams. Exported so a future
// stream binds it directly rather than forking a third copy of the fold.
export {
    EntityStateStore,
    applyDeltaToEntityMap,
    foldEntityDeltas,
    DISCONNECTED_ENTITY_STATE,
} from './stores/realtime/entity-state-store';
export type {
    EntityStateConnection,
    EntityStateStoreConfig,
    EntityStateCatchupResultBase,
} from './stores/realtime/entity-state-store';

// Shared clock-tick hook (BUG-887): realtime lot hooks call this so
// time-dependent lot-phase derivations re-run on a clock tick without each
// consuming view wiring its own interval. See `./react/useRealtimeClockTick`.
export {
    useRealtimeClockTick,
    __resetRealtimeClockTickersForTests,
} from './hooks/realtime/useRealtimeClockTick';

// Test harness re-export. The canonical home is `@auctogon/core/realtime/testing`,
// but CI's vite import-analysis intermittently fails to resolve workspace
// package-exports subpaths that point into a sub-directory. Re-exporting
// from the main barrel here lets consumers import from `@auctogon/core/realtime`
// (the path every other test already uses) without each tree carrying its
// own bespoke alias map. Production code should NOT import this — it pulls
// in the in-memory transport bytes; the dedicated `./testing` subpath stays
// the production entry point.
export { createInMemoryRealtimeTransport } from './transport/realtime/in-memory-transport';
export {
    createManualNotifyScheduler,
    type ManualNotifyScheduler,
} from './transport/realtime/manual-notify-scheduler';
export type { NotifyScheduler } from './stores/realtime/entity-state-store';

export {
    mapUserRegistrationsSnapshot,
} from './mappers/realtime/user-registrations-snapshot';
export type {
    BidRegistrationRow,
    BidRegistrationSnapshotItem,
    BidRegistrationSnapshotStatus,
    UserRegistrationsSnapshot,
} from './mappers/realtime/user-registrations-snapshot';

export {
    EMPTY_BID_POSITIONS_SNAPSHOT,
    mapBidPositionsSnapshot,
} from './mappers/realtime/bid-positions-snapshot';
export type {
    BidPositionItem,
    BidPositionRow,
    BidPositionsSnapshot,
} from './mappers/realtime/bid-positions-snapshot';

export {
    EMPTY_AUCTION_SNAPSHOT,
    mapAuctionSnapshot,
} from './mappers/realtime/auction-snapshot';
export type {
    AuctionSnapshot,
    AuctionSnapshotRow,
    RingIndexEntry,
} from './mappers/realtime/auction-snapshot';

// BUG-1299: the shared `AuctionSnapshot -> effective state` selector, lifted out
// of bidder-web so the admin clerk console reads the same derivation.
export {
    selectAuctionEffectiveState,
    DEFAULT_AUCTION_EFFECTIVE_STATE,
} from './isomorphic/realtime/auction-effective-state';
export type { AuctionEffectiveState } from './isomorphic/realtime/auction-effective-state';

export {
    decodeLotSnapshot,
    EMPTY_LOT_SNAPSHOT,
    mapLotSnapshot,
} from './mappers/realtime/lot-snapshot';
export type {
    LotSnapshot,
    LotSnapshotDecoded,
    LotSnapshotDecodeResult,
    LotSnapshotRow,
} from './mappers/realtime/lot-snapshot';


export { mapBidLedgerEntryPublic } from './mappers/realtime/bid-ledger';
// The one encoder / decoder for a row's money on every payload.
export { decodeWireAmount, encodeRowMoney } from './mappers/realtime/money-wire-amount';
export type { WireAmountDecode } from './mappers/realtime/money-wire-amount';
export type {
    BidLedgerEntryEnrichment,
    BidLedgerEntryRow,
    BidLedgerStatus,
    PublicBidLedgerEntry,
} from './mappers/realtime/bid-ledger';

// BL-1487 — the unread-counts snapshot's one declared shape (rule #32 seam).
export { EMPTY_CUSTOMER_UNREADS_SNAPSHOT } from './mappers/realtime/customer-unreads-snapshot';
export type { CustomerUnreadsSnapshot } from './mappers/realtime/customer-unreads-snapshot';
export { EMPTY_STAFF_UNREADS_SNAPSHOT } from './mappers/realtime/staff-unreads-snapshot';
export type { StaffUnreadsSnapshot } from './mappers/realtime/staff-unreads-snapshot';

export { mapCustomerToastEntry } from './mappers/realtime/customer-toast';

// BL-2473 — the staff mention pop-up's ledger payload.
export { mapStaffToastEntry } from './mappers/realtime/staff-toast';
export type { StaffToastRow, PublicStaffToastEntry } from './mappers/realtime/staff-toast';
export type {
    CustomerToastRow,
    PublicCustomerToastEntry,
} from './mappers/realtime/customer-toast';

export {
    ADMIN_LOT_STATE_MONEY_FIELDS,
    decodeAdminLotStateDelta,
    mapAdminLotState,
} from './mappers/realtime/admin-lot-state';
export type {
    AdminLotStateDelta,
    AdminLotStateDeltaDecoded,
    AdminLotStateMoneyField,
    AdminLotStateRow,
} from './mappers/realtime/admin-lot-state';

export { decodeAdminBidLedgerEntry, mapAdminBidLedgerEntry } from './mappers/realtime/admin-bid-ledger';
export type {
    AdminBidLedgerCatchupResult,
    AdminBidLedgerEntry,
    AdminBidLedgerEntryDecoded,
    AdminBidLedgerEntryDecodeResult,
    AdminBidLedgerEntryRow,
} from './mappers/realtime/admin-bid-ledger';

export { mapAdminRegistration } from './mappers/realtime/admin-auction-registrations';
export type {
    AdminRegistrationDelta,
    AdminRegistrationRow,
} from './mappers/realtime/admin-auction-registrations';

export { mapAdminConversation } from './mappers/realtime/admin-conversations';
export type {
    AdminConversationDelta,
    AdminConversationRow,
} from './mappers/realtime/admin-conversations';

export { decodeAdminMaxBidDelta, mapAdminMaxBid } from './mappers/realtime/admin-max-bids';
export type {
    AdminMaxBidDelta,
    AdminMaxBidDeltaDecoded,
    AdminMaxBidDeltaDecodeResult,
    AdminMaxBidRow,
    AdminMaxBidStatus,
} from './mappers/realtime/admin-max-bids';

// BL-410 / ADR 046 step 5 — the server-asserted online roster. Returns
// `customer_user.id`s; presence is user-anchored and carries no paddle.
export { usePresenceRoster } from './hooks/realtime/usePresenceRoster';
export { createPresenceTokenFetcher } from './wiring/realtime/presence-token-fetcher';
export { PresenceStore } from './stores/realtime/presence-store';
export type { PresenceUpdatedEvent } from './types/realtime/events/presence';
